<template>
  <AccountLayout>
    <div class="flex flex-wrap w-full">
      <article ref="account" class="account__article w-full lg:w-4/6">
        <Alert v-if="errorMessage.length > 0" class="mb-8" alert-type="error">
          <div class="flex items-center text-xs text-b30">
            {{ errorMessage }}
          </div>
        </Alert>
        <Alert v-if="isVatCalculationError" class="mb-8" alert-type="error">
          <div class="text-xs text-b30">
            <div class="font-bold">Please try again in a few minutes.</div>
            <div>
              Looks like there’s been a technical issue. We’re working on it and will be back again
              soon.
            </div>
          </div>
        </Alert>
        <div
          v-if="updatedSuccessfully"
          class="flex items-center justify-center p-2 mb-6 text-center text-white bg-ma-green-2 rounded-md">
          Your details have been updated successfully.
        </div>
        <h2 class="leading-none mb-6 text-[26px] font-bold text-white">Personal Details</h2>
        <Form
          ref="form"
          :user="user"
          @validatedForm="validatedForm = $event"
          @change:country="countryProvider = $event"
          @addressValidated="addressValidated = $event" />
        <ChangePassword
          ref="password"
          :updated-successfully="updatedSuccessfully"
          @validatedPassword="validatedPassword = $event" />
        <div class="flex mt-8">
          <button
            ref="submitButton"
            class="al-btn al-btn--primary w-48 px-3 flex align-center justify-center font-bold"
            :class="'disabled:bg-white disabled:bg-opacity-20'"
            :disabled="disableSubscribeBtn"
            @click="submitForm">
            <span v-if="invoiceEstimateLoading">Verifying...</span>
            <span v-else> Save Changes</span>
            <div
              v-if="submitting || invoiceEstimateLoading"
              class="flex items-center justify-center ml-4">
              <div class="w-2 h-2 border-b-2 border-white rounded-full animate-spin" />
            </div>
          </button>
        </div>
      </article>
      <div v-if="showDeleteAccount" class="w-full lg:w-2/6 flex justify-items-end items-end">
        <div
          class="flex justify-end mr-4 w-full cursor-pointer delete-account"
          @click="openModal = true">
          Delete Account
        </div>
        <DeleteUserModal
          v-if="openModal"
          :delete-account="deleteAccount"
          :submitting="submitting"
          @close="openModal = false" />
      </div>
    </div>
  </AccountLayout>
</template>

<script>
import axios from 'axios';
import {
  AVALARA_MESSAGE,
  ESTIMATED_PRICE_ERROR_MESSAGES,
  INTERNAL_ERROR_MESSAGE,
  VAT_ID_NOT_VALID,
} from '@/constants';
import Alert from '@/components/Alert.vue';
import analyticsEvents from '@/utils/analyticsEvents';
import { eventTracker } from '@/utils/eventTracker';
import Form from './components/form.vue';
import AccountLayout from '../components/account-layout.vue';
import ChangePassword from './components/password.vue';
import DeleteUserModal from './components/delete-user.vue';
import eventsUtils from '@/mixins/events/eventsUtils.vue';

export default {
  metaInfo() {
    return {
      title: 'Account - Personal Details | Motion Array',
    };
  },
  components: {
    AccountLayout,
    Form,
    ChangePassword,
    DeleteUserModal,
    Alert,
  },
  mixins: [eventsUtils],
  data() {
    return {
      submitting: false,
      errors: {},
      errorMessage: '',
      updatedSuccessfully: false,
      validatedForm: false,
      validatedPassword: false,
      openModal: false,
      countryProvider: {},
      invoiceEstimateError: {},
      invoiceEstimateLoading: false,
      isVatCalculationError: false,
      addressValidated: false,
      billingInformation: {
        isZipValid: false,
      },
      isInternalError: false,
    };
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
    isAutocompleteLoaded() {
      return this.$store.getters.isAutocompleteLoaded;
    },
    showDeleteAccount() {
      return (
        !this.user?.paying_user &&
        !this.user?.is_team_admin &&
        !this.user?.is_seller_with_approved_submission
      );
    },
    disableSubscribeBtn() {
      return (
        this.submitting ||
        this.isVatCalculationError ||
        !!this.invoiceEstimateError?.message ||
        this.invoiceEstimateLoading ||
        this.isInternalError
      );
    },
    isTeamAdmin() {
      return this.$store.state.user?.is_team_admin;
    },
    showAbn() {
      return this.countryProvider?.id === this.$store.state.subscription.countryAustralia?.id;
    },
  },
  watch: {
    countryProvider() {
      this.invoiceEstimateError = {};
      this.errorMessage = '';
      this.errors = {};
      this.isVatCalculationError = false;
      this.updatedSuccessfully = false;
      this.$refs.form.validateAddressStatus();
    },
    addressValidated(newVal) {
      if (newVal) {
        this.errorMessage = '';
        this.invoiceEstimateError = {};
        this.isVatCalculationError = false;
        this.updatedSuccessfully = false;
        this.$nextTick(() => {
          this.avalaraAddressValidation();
        });
      }
    },
  },
  created() {
    if (!this.isAutocompleteLoaded) {
      this.$store.dispatch('loadPlaceAutocomplete');
    }
  },
  methods: {
    scrollToTop() {
      this.$nextTick(() => {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth',
        });
      });
    },
    async deleteAccount() {
      if (this.submitting) {
        return;
      }

      this.errorMessage = '';
      this.submitting = true;

      try {
        const result = await axios.delete('/api/v3/account');

        if (result.status === 200) {
          window.location.replace('/account/logout');
        }
        return result;
      } catch (error) {
        this.openModal = false;
        this.errorMessage =
          error.response.data.message || 'Something went wrong. Please try again later.';
        this.scrollToTop();
        return error;
      } finally {
        this.submitting = false;
      }
    },
    async submitForm() {
      this.trackSaveEvent();
      if (this.submitting) {
        return;
      }

      this.errorMessage = '';
      this.errors = {};
      this.updatedSuccessfully = false;

      this.$refs.form.validateForm();
      this.$refs.password.validateForm();

      if (!this.$refs.form.isFormValid || !this.$refs.password.isPasswordValid) {
        this.scrollToTop();
        return;
      }
      this.trackVatFillCompleteEvent();

      this.submitting = true;

      try {
        const {
          country,
          user_state,
          first_name,
          last_name,
          user_address,
          user_city,
          ...restFields
        } = { ...this.$refs.form.fields };
        const { current_password, password, password_confirmation } = {
          ...this.$refs.password.fields,
        };

        const payload = {
          ...restFields,
          country_id: country?.id || null,
          firstname: first_name,
          lastname: last_name,
          state: user_state.name || '',
          address: user_address,
          city: user_city,
          password,
          password_confirmation,
          current_password,
        };

        const result = await axios.put('/api/v3/account', payload);
        this.errorMessage = '';
        this.updatedSuccessfully = true;
        this.submitting = false;
        this.errors = {};
        this.scrollToTop();
        this.trackSaveChangesEvent();
        return result;
      } catch (error) {
        this.submitting = false;
        if (error.response.status >= 400) {
          this.errorMessage =
            error.response.data.message || 'Something went wrong. Please try again later.';
          // fields errors
          const errorsData = {};
          if (Object.keys(error.response.data.errors || {}).length > 0) {
            Object.keys(error.response.data.errors).forEach(input => {
              // eslint-disable-next-line prefer-destructuring
              errorsData[input] = error.response.data.errors[input][0];
            });
          }
          this.$refs.form.errors = errorsData;
          this.$refs.password.errors = errorsData;
        } else {
          this.errorMessage = 'Something went wrong. Please try again later.';
        }
        this.scrollToTop();
      }
    },
    // avalara address validation - we don't have specific API for this so using estimate/create-subscription
    // payload quantity is not important, just need to pass data and check address, using only error result
    async avalaraAddressValidation() {
      let planId;
      if (this.isTeamAdmin) {
        planId = 'team_yearly_10_percent_discount';
      } else {
        planId = 'yearly_unlimited_2020';
      }
      const payload = {
        country_id: this.countryProvider?.id,
        plan_id: planId,
        zip: this.$refs.form?.fields.zip,
        state_name: this.$refs.form?.fields.user_state?.name,
        city: this.$refs.form?.fields.user_city,
        address: this.$refs.form?.fields.user_address,
        quantity: this.isTeamAdmin ? 2 : null,
        vat_id: this.$refs.form?.fields.vat_id || '',
        countryCode: this.countryProvider?.code,
      };

      this.invoiceEstimateError = {};
      this.invoiceEstimateLoading = true;
      this.errorMessage = '';
      this.errors = {};
      this.isVatCalculationError = false;
      this.updatedSuccessfully = false;
      this.isInternalError = false;

      try {
        const { data } = await axios.post('/api/payment/estimate/create-subscription', payload);

        if (data.status >= 400) {
          throw new Error(data);
        }

        const { isError, errorCode } = data.data.internalErrors || {};
        if (isError) {
          const internalError = ESTIMATED_PRICE_ERROR_MESSAGES.includes(errorCode);
          if (errorCode !== VAT_ID_NOT_VALID) {
            this.isInternalError = internalError;
          }
          if (internalError) {
            this.globalErrorMessage = INTERNAL_ERROR_MESSAGE;
          }
          this.$store.dispatch('setIsVatIdEstimationError', isError);
        }

        if (this.addressValidated) {
          this.billingInformation.isZipValid = true;
          this.addressValidationEventHandler();
        }
      } catch (error) {
        console.error('account details address validation error', error.response.data);
        this.invoiceEstimateError = error.response.data;
        this.errorMessage = error.response.data.message;
        const avalaraError = !!this.invoiceEstimateError?.message.includes(AVALARA_MESSAGE);
        if (avalaraError) {
          this.isVatCalculationError = avalaraError;
        }
        // we need to show that zip is false only if we get exact error message
        if (this.$refs.form?.errors?.zip) {
          this.billingInformation.isZipValid = false;
        }
        this.addressValidationEventHandler();
        this.scrollToTop();
      } finally {
        this.invoiceEstimateLoading = false;
      }
    },
    addressValidationEventHandler() {
      const { country, user_address, user_city, user_state, zip, vat_id, company } =
        this.$refs.form.fields;
      eventTracker(analyticsEvents.addressFillComplete, {
        input_country: country?.name,
        input_address: user_address,
        input_city: user_city,
        input_state: user_state?.name,
        input_zip: zip,
        input_abn: vat_id,
        input_company_name: company,
        'zip/postal_code_valid': this.billingInformation.isZipValid,
        auto_fill: this.$refs.form?.addressPopulated,
        section_title: 'Personal Details',
        avalara_error:
          this.isVatCalculationError?.message || this.invoiceEstimateError?.message || '',
      });
    },
    trackVatFillCompleteEvent() {
      eventTracker(analyticsEvents.vatFillComplete, {
        previous_value: this.$refs.form.fieldsPreviousValue?.vat_id || '',
        input_placeholder: this.showAbn ? 'ABN' : 'Vat ID',
        abn_valid: !this.errors?.vat_id,
        error_text: this.errors?.vat_id,
        section_title: 'invoice details',
        input_value: this.$refs.form.fields.vat_id,
        artlist_ABN: this.$refs.form.fields.vat_id,
        click_source: 'account page',
        vat_companyName: this.$refs.form.fields.company,
        vat_vatNumber: this.$refs.form.fields.vat_id,
      });
    },
    trackSaveEvent() {
      eventTracker(analyticsEvents.buttonClicked, {
        click_source: 'account page',
        button_name: 'Save Changes',
        is_valid: this.validatedForm && this.validatedPassword,
        error_message: this.errorMessage,
      });
    },
    trackSaveChangesEvent() {
      const updatedFields = this.getUpdatedFields();
      eventTracker(analyticsEvents.actionCompleted, {
        click_source: 'account page',
        action_name: 'personal detailed updated',
        action_text_response: 'Your details have been updated successfully',
        inputs_edited: updatedFields,
      });
    },
      },
};
</script>

<style scoped>
.text-\[26px\] {
  font-size: 26px;
}
.al-btn:disabled {
  opacity: 0.7;
  cursor: auto;
}
.delete-account {
  @apply text-b78;
}
</style>
